import type { CrossfadeParams } from 'svelte/transition';
import {
  quintOut,
  quadOut,
  quadIn,
  backOut,
  linear,
  backIn,
  sineIn,
  sineOut,
} from 'svelte/easing';
import { Animation, SPRING, animationSpeed } from './animations/utils.svelte';
import { springIn, springOut } from './animations/springs';

export const flyIn = (
  node: HTMLElement,
  { y = 20, duration = 500, delay = 0 },
) => ({
  duration,
  delay,
  easing: quintOut,
  css: (t: number) =>
    `transform: translateY(${t * y}px); transform: scale(${t})`,
});

const tileFlipDuration = $derived(animationSpeed.ms / 2);

export const flipOverAnimation = new Animation(0);

export const flipOver = (node: HTMLElement, params: FlipParams) => {
  if (!params.enabled) return {};
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;
  const width = node.clientWidth;
  const duration = tileFlipDuration;
  const totalDelay = (params.delay ?? 0) + (params.intro ? duration : 0);
  flipOverAnimation.queue(duration + totalDelay);
  return {
    delay: totalDelay,
    duration,
    easing: params.intro ? sineOut : sineIn,
    css: (t: number, u: number) => `
      transform:
        ${transform}
        perspective(${Math.max(width, 50) * 10}px)
        rotateY(${(params.intro ? 1 : -1) * u * 90}deg);
      ${params.fade ? `opacity: ${t};` : ''}
    `,
  };
};

export type Direction = 'up' | 'down' | 'left' | 'right';

type FlipSwapsParams = {
  duration?: number;
  delay?: number;
  delta?: number;
  transition: 'in' | 'out';
  direction: Direction;
};

export const flipSwaps = (
  node: HTMLElement,
  { duration = 200, transition, direction, delta, delay = 0 }: FlipSwapsParams,
) => {
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;
  const width = node.clientWidth;

  let mult = transition === 'in' ? 1 : -1;
  if (direction === 'down') mult *= -1;

  let css;
  if (['up', 'down'].includes(direction)) {
    css = (t: number, u: number) => `
      transform:
        ${transform}
        translateY(${((-mult * width) / 4) * u}px)
        perspective(50px)
        translateY(${mult * width * u}px)
        rotateX(${-mult * u * 45}deg);
      opacity: ${quadIn(t)};
    `;
  } else {
    css = (t: number, u: number) => `
      transform:
        ${transform}
        perspective(${Math.max(width, 50) * 10}px)
        rotateY(${mult * u * 90}deg);
      opacity: ${1 + t / 2};
    `;
  }
  return {
    delay: delay + (transition === 'in' ? duration / 2 : 0),
    duration,
    easing: linear,
    css,
  };
};

type FlipParams = Omit<CrossfadeParams, 'duration'> & {
  intro: boolean;
  enabled: boolean;
  fade?: boolean;
};

export const allClearFlip = (node: HTMLElement) => {
  const style = getComputedStyle(node);
  const transform = style.transform === 'none' ? '' : style.transform;
  const width = node.clientWidth;
  return {
    delay: 0,
    duration: 500,
    easing: linear,
    css: (t: number, u: number) => {
      return `
      transform:
        ${transform}
        rotateY(${Math.sqrt(t) * 360}deg)
        translateY(${50 * backIn(u)}px)
        scale(${backOut(t)})
    `;
    },
  };
};

export const gaugeIn = (
  node: SVGCircleElement,
  { duration = 1000, delay = 0, easing = quadIn },
) => {
  return {
    delay,
    duration,
    easing,
    css: (t: number) => `
      opacity: ${t};
    `,
    //transform: scale(${f(u, r)}) rotate(135, ${r * f(u, r)}, ${r * f(u, r)});
  };
};

export const gaugeOut = (
  node: SVGCircleElement,
  { duration = 1000, delay = 0, easing = quadIn },
) => {
  // const r = parseFloat(node.getAttribute('r'));
  const w = parseFloat(node.getAttribute('stroke-width')!);

  return {
    delay,
    duration,
    easing,
    css: (t: number) => `
      stroke-width: ${0.5 * (1 + t) * w};
    `,
    //transform: scale(${f(u, r)}) rotate(135, ${r * f(u, r)}, ${r * f(u, r)});
  };
};

const getRotation = (node: HTMLElement) => {
  const r = getComputedStyle(node).transform;
  if (!r) return 0;
  const values = r.split('(')[1].split(')')[0].split(',');
  return Math.round(Math.atan2(+values[1], +values[0]) * (180 / Math.PI));
};

const hex = (x: number): string => {
  const y = x.toString(16);
  return y.length == 1 ? '0' + y : y;
};

const getColorInterp = (color1: string, color2: string, ratio: number) => {
  const r = Math.ceil(
    parseInt(color1.substring(0, 2), 16) * ratio +
      parseInt(color2.substring(0, 2), 16) * (1 - ratio),
  );
  const g = Math.ceil(
    parseInt(color1.substring(2, 4), 16) * ratio +
      parseInt(color2.substring(2, 4), 16) * (1 - ratio),
  );
  const b = Math.ceil(
    parseInt(color1.substring(4, 6), 16) * ratio +
      parseInt(color2.substring(4, 6), 16) * (1 - ratio),
  );

  return `#${hex(r)}${hex(g)}${hex(b)}`;
};

export const shrinkPoints = (
  node: HTMLElement,
  { duration = 500, delay = 0 } = {},
) => {
  return {
    duration,
    delay,
    easing: quadOut,
    css: (t: number) => `
      transform: scale(${(1 + t) / 2});
      opacity: ${t};
    `,
  };
};

export const starInAnimation = new Animation();

export const starIn = (node: HTMLElement, { duration = 500, delay = 0 }) => {
  starInAnimation.queue(duration + delay);
  return {
    duration,
    delay,
    easing: linear,
    css: (t: number) => `
      transform: scale(${backOut(t)});
      opacity: ${t};
    `,
  };
};

export const starOutAnimation = new Animation();

export const starOut = (node: HTMLElement, { duration = 500, delay = 0 }) => {
  starOutAnimation.queue(duration + delay);
  return {
    duration,
    delay,
    easing: linear,
    css: (t: number) => `
      transform:
        perspective(50px)
        rotateY(${360 * sineIn(t)}deg);
      opacity: ${t};
    `,
  };
};

export const starInGameOver = (
  node: HTMLElement,
  { duration = 500, delay = 0 },
) => ({
  duration,
  delay,
  easing: linear,
  css: (t: number) => `
    transform:
      rotateY(${360 * quadOut(t)}deg)
      scale(${backOut(t)});
  `,
});

const DEG = 30;
const SCALE = 100;

export const gaugeStarIn = (node: HTMLElement) => {
  const rotation = getRotation(node);
  const { duration, tickToValue } = springIn(0, SCALE, {
    ...SPRING,
    stiffness: 0.4,
    damping: 0.8,
    fps: 60,
  });
  return {
    duration,
    css: (t: number, u: number) => `
      opacity: ${Math.min(2 * t, 1)};
      transform: rotate(${rotation + DEG * (1 - tickToValue(t) / SCALE)}deg);
      color: ${getColorInterp('FFBF00', '1B5E20', Math.max(2 * (t - 0.5), 0))}
    `,
  };
};

export const gaugeStarOut = (node: HTMLElement) => {
  const rotation = getRotation(node);
  const { duration, tickToValue } = springOut(0, SCALE, {
    ...SPRING,
    stiffness: 0.4,
    damping: 0.8,
    fps: 60,
  });
  return {
    duration,
    css: (t: number, u: number) => `
      opacity: ${1 - tickToValue(t) / SCALE};
      transform: rotate(${rotation + (DEG * tickToValue(t)) / SCALE}deg);
      color: ${getColorInterp('B71C1C', 'FFBF00', Math.max(2 * (u - 0.5), 0))};
    `,
  };
};

const ROTATE_ANGLE = 30;

export const levelIn = (node: SVGElement) => {
  const duration = 200;
  return {
    duration,
    delay: duration,
    ease: linear,
    css: (t: number, u: number) => `
      transform: rotate(${-ROTATE_ANGLE * backIn(u)}deg);
      transform-origin: center;
      opacity: ${Math.sqrt(t)};
    `,
  };
};

export const levelOut = (node: SVGElement) => {
  const duration = 200;
  return {
    duration,
    delay: 0,
    ease: linear,
    css: (t: number, u: number) => `
      transform: rotate(${ROTATE_ANGLE * backIn(u)}deg);
      transform-origin: center;
      opacity: ${Math.sqrt(t)};
    `,
  };
};

export function slideInOnce(node: HTMLElement) {
  node.style.opacity = '0';
  node.style.transform = 'translateY(0.75em)';
  node.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        node.style.opacity = '1';
        node.style.transform = 'translateY(0)';
        observer.disconnect();
      }
    },
    { threshold: 0.1 },
  );
  observer.observe(node);
  return { destroy: () => observer.disconnect() };
}

export const gameModeIn = (node: HTMLElement, { delay = 0 }) => {
  const angle = 90;
  const { duration, tickToValue } = springIn(300, 0, {
    ...SPRING,
    stiffness: 0.15,
    damping: 0.8,
  });
  return {
    duration,
    delay,
    ease: linear,
    css: (t: number, u: number) => `
      transform: rotateX(${(angle * tickToValue(t)) / 300}deg) translateY(${tickToValue(t)}px);
      opacity: ${t};
    `,
  };
};
